import React, { useEffect, useState } from 'react';
import './BookingConfirmation.scss';
import UserIcon from '@/assets/img/user-icon.png';
import {
  AppointmentData,
  BookAppointmentResponse,
  Business,
  Service,
  WeightPriceConfiguration,
} from '../types';
import Utils from '@/utils/util';
import { useStripe } from '@stripe/react-stripe-js';
import { useBookAppointMent } from '../api/bookAppointment';
import moment from 'moment';
import { Loader } from '@/components/Loader/Loader';
import { BackNav } from '@/components/BackNavigation/BackNavigation';
import CatDefaultIcon from '@/assets/img/v2/pet-cat-default-icon.svg';
import DogDefaultIcon from '@/assets/img/v2/pet-dog-default-icon.svg';

import { HOST_URL } from '@/config';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { PetDetail } from './PetDetail';
import { PetConfirmationDisplay } from './PetConfirmationDisplay';
import i18n from '@/i18next';
import { useTranslation } from 'react-i18next';
import { businessCurrency } from '@/features/Language/api/getCountryCurrency';

type BookingConfirmationProps = {
  business?: Business;
  data?: AppointmentData;
  onBack: (page: 'payment-method' | 'pet-profile') => void;
  onComplete: (data: AppointmentData) => void;
};
export const BookingConfirmation = ({
  business,
  data,
  onBack,
  onComplete,
}: BookingConfirmationProps) => {
  const [showServiceDetails, setShowServiceDetails] = useState<boolean>(false);
  const stripe = useStripe();
  const { t, i18n } = useTranslation();

  // useEffect(() => {
  //   if (business) {
  //     const language = business?.country === 'New Zealand' ? 'en-NZ' : 'en-US';
  //     i18n.changeLanguage(language).then(() => {
  //       console.log(`Language changed to: ${i18n.language}`);
  //     });
  //   }
  // }, [business?.country]);
  const bookAppointmentMutation = useBookAppointMent();
  useEffect(() => {
    Utils.scrollToTop();
  }, []);
  const onBookAppoinment = async () => {
    if (!bookAppointmentMutation.isLoading) {
      const duration = Utils.getDurationFromService(data?.service);
      const request = {
        email: data?.parentProfile?.email ? data?.parentProfile?.email.toLowerCase() : '',
        altEmails: data?.parentProfile?.altEmails ? data?.parentProfile?.altEmails : [],
        phone: data?.parentProfile?.phone ? data?.parentProfile?.phone : '',
        startTime: data?.slot?.start ? new Date(data?.slot?.start) : undefined,
        endTime: data?.slot?.end ? new Date(data?.slot?.end) : undefined,
        businessUserId: Number(data?.provider?.id),
        serviceId: Number(data?.service?.id),
        summary: data?.service?.name,
        description: `${data?.service?.name} for ${Utils.minutesToDhm(duration)}`,
        tz: data?.tz ? data?.tz : moment.tz.guess(true),
        businessId: data?.business?.id,
        //petId: data?.petProfile?.id ? Number(data?.petProfile?.id) : 0,
        petDetails: data?.petProfiles,
        duration: data?.duration,
        paymentMethodId:
          data?.paymentCard?.stripePaymentToken ||
          data?.paymentCard?.squareCardId ||
          data?.creditCard?.stripePaymentToken ||
          data?.creditCard?.squareCardId ||
          '',
        addonSids: Utils.getSelectedAddonsIds(data?.selectedAddons),
        startDate: moment(data?.startDate).format('YYYY-MM-DD'),
        endDate: moment(data?.endDate).format('YYYY-MM-DD'),
        petCount: data?.noOfPets ? data?.noOfPets : 0
      };

      let bookingResponse = await bookAppointmentMutation.mutateAsync({
        data: request,
        serviceType: data?.selectedServiceType,
      });
      if (bookingResponse.paymentIntent && bookingResponse.paymentIntent.next_action && stripe) {
        const { error: confirmError, paymentIntent }: any = await stripe.confirmCardPayment(
          bookingResponse.paymentIntent.client_secret,
          {
            payment_method: bookingResponse.paymentIntent.payment_method,
          }
        );
        if (confirmError) {
          console.error(confirmError);
          await bookAppointmentMutation.mutateAsync({
            data: request,
          });
        } else {
          bookingResponse = await bookAppointmentMutation.mutateAsync({
            data: request,
          });
          onBookingConfirmed(bookingResponse);
        }
      } else {
        onBookingConfirmed(bookingResponse);
      }
    }
  };

  const onBookingConfirmed = (res: BookAppointmentResponse) => {
    if (data) {
      (data.id = res.appointment.id), // ? res.appointment.id : res.id
        (data.room = res.appointment.room);
      data.startDate = new Date(res.appointment.startTime);
      data.endDate = new Date(res.appointment.endTime);
      data.petProfiles = res.appointment?.pets;
      data.intakeFormLink = res.appointment?.intakeFormLink;
      onComplete(data);
    }
  };
  const onBackArrowClick = () => {
    if (data?.selectedServiceType === Utils.SERVICE_TYPES.GROOMING) {
      onBack &&
        onBack(data?.service?.price && data?.service?.price > 0 ? 'payment-method' : 'pet-profile');
    } else {
      onBack && onBack(data?.cost?.fee && data?.cost?.fee > 0 ? 'payment-method' : 'pet-profile');
    }
  };
  const getAdvancePayment = () => {
    if (data?.selectedServiceType === Utils.SERVICE_TYPES.GROOMING) {
      if (data?.business) {
        const advBookingPercentage = data?.selectedLocation?.advBookingPercentage ? parseFloat(data?.selectedLocation?.advBookingPercentage) : 0;
        const advBookingAmount = data?.selectedLocation?.advBookingAmount ? parseFloat(data?.selectedLocation?.advBookingAmount) : 0;
        let totalPayment = 0;
        totalPayment = Utils.getPriceFromService(data.service);
        const addonsPrice = data.selectedAddons ? Utils.getAddonsPrice(data.selectedAddons) : 0;
        totalPayment = parseFloat(totalPayment.toString()) + addonsPrice;
        if (advBookingPercentage > 0) {
          const percentage = (advBookingPercentage / 100) * totalPayment;
          return Utils.roundDecimal(percentage);
        } else if(advBookingAmount > 0) {
          return Utils.roundDecimal(advBookingAmount);
        } else {
          return Utils.roundDecimal(totalPayment);
        }
      }
    }
    if (
      data?.selectedServiceType === Utils.SERVICE_TYPES.DAY_CARE ||
      data?.selectedServiceType === Utils.SERVICE_TYPES.BOARDING
    ) {
      return data.cost && data.cost?.advance ? Utils.roundDecimal(data.cost?.advance) : Utils.roundDecimal(0);
    }
  };

  const customerCard = data?.paymentCard ? data.paymentCard : data?.creditCard
  const last4Digit = customerCard?.number.split('-')[3];
  let servicePrice = 0;
  if (data?.selectedServiceType === Utils.SERVICE_TYPES.GROOMING) {
    servicePrice = Utils.getPriceFromService(data?.service); 
  } else {
    servicePrice = data?.cost?.fee ? data?.cost?.fee : 0;
  }
  const duration = Utils.getDurationFromService(data?.service);

  return (
    <div className="booking-confirmation">
      <div className="profile-container">
        <BackNav onClick={onBackArrowClick} />
        <h5>{t('Booking Confirmation')}</h5>
        <p className="desc">{t('Verify your service details below')}</p>
        {(data?.selectedServiceType === Utils.SERVICE_TYPES.GROOMING || data?.service) && (
          <div className="service-details">
            <div className="header">
              <label>
                {data?.service?.name}
                {data?.selectedAddons && data?.selectedAddons.length > 0 && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-id" className="service-provider-tooltip">
                        <div className="addons-tooltips">
                          <p>{t('Add-ons')}</p>
                          {data?.selectedAddons.map((addons: Service) => (
                            <ul>
                              <li>{addons.name}</li>
                            </ul>
                          ))}
                        </div>
                      </Tooltip>
                    }
                  >
                    <span className="addons-service-count" style={{ cursor: 'pointer' }}>
                      +{data?.selectedAddons.length}
                    </span>
                  </OverlayTrigger>
                )}
                {/* {data?.selectedAddons?.length && (
                <span className="addons-service-count" style={{ cursor: 'pointer' }}>
                  +{data?.selectedAddons?.length}
                </span>
              )} */}
              </label>
              <label className="pink" onClick={() => setShowServiceDetails(!showServiceDetails)}>
                {showServiceDetails ? t('- Less Details') : t('+ More Details')}
              </label>
            </div>
            <p className={`service-desc ${showServiceDetails ? '' : 'clip'}`}>
              {data?.service?.description}
            </p>
            <div className={`details ${showServiceDetails ? '' : 'hide'}`}>
              <div className="detail">
                <label>{t('Service Provider')}</label>
                <div className="host-details">
                  <img src={data?.provider?.avatarUrl ? data.provider.avatarUrl : UserIcon} />
                  <div>
                    <p>{data?.provider?.name}</p>
                    <p>{data?.provider?.email}</p>
                  </div>
                </div>
              </div>
              <div className="detail">
                <label>{t('Duration')}</label>
                <p>{duration && Utils.minutesToDhm(duration)}</p>
              </div>
              {data?.selectedAddons && data?.selectedAddons.length > 0 && (
                <div className="detail addons">
                  <label>{t('Add-on services')}</label>
                  <div className="duration-date">
                    <ul className="addon-services">
                      {data?.selectedAddons.map((addonService) => (
                        <li>{addonService.name}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="details additional">
          <div className="">
            {!data?.room && data?.selectedServiceType === Utils.SERVICE_TYPES.GROOMING && (
              <div className="detail">
                <label>{t('Service Detail')}</label>
                <p>
                  <span className="seperator">
                    {Utils.getOnlyDate(data?.slot?.start, data?.selectedLocation?.timezone, 'MMMM DD, YYYY')}{' '}
                  </span>
                  <span>
                    {' '}
                    {data?.slot &&
                      Utils.getOnlyDate(data?.slot.start, data?.selectedLocation?.timezone, 'hh:mm A')}{' '}
                    -{' '}
                    {data?.slot && Utils.getOnlyDate(data?.slot.end, data?.selectedLocation?.timezone, 'hh:mm A')}{' '}
                    <span className="text-gray-500 text-sm">
                      {Utils.getTzAbbr(data?.selectedLocation?.timezone)}
                    </span>
                  </span>
                </p>
              </div>
            )}
            {data?.selectedServiceType === Utils.SERVICE_TYPES.BOARDING && (
              <>
                <div className="detail">
                  <label>{t('Service Detail')}</label>
                  <p>
                    <span className="seperator">Boarding Service</span>
                    {data?.cost?.days && <span>{data?.cost?.days} {data?.cost?.days > 1 ? "Days" : "Day"}</span>}
                  </p>
                </div>
                <div className="detail">
                  <label>{t('Check-in Time')}</label>
                  <p>
                    {data?.startDate &&
                      Utils.formatDateStringTz(data?.startDate, undefined, "DD MMM 'yy ")}
                      {Utils.getTimeLabel(data?.selectedLocation?.boardingCheckIn).replace(/\s+/g, '').toLowerCase()}
                  </p>
                </div>
                <div className="detail">
                  <label>{t('Check-out Time')}</label>
                  <p>
                    {data?.endDate &&
                      Utils.formatDateStringTz(data?.endDate, undefined, "DD MMM 'yy ")}
                      {Utils.getTimeLabel(data?.selectedLocation?.boardingCheckOut).replace(/\s+/g, '').toLowerCase()}
                  </p>
                </div>
              </>
            )}
            {data?.selectedServiceType === Utils.SERVICE_TYPES.DAY_CARE && (
              <>
                <div className="detail">
                  <label>{t('Service Detail')}</label>
                  <p>
                    <span className="seperator">{t('Day-Care Service')}</span>
                    <span> {Utils.minutesToDhm(data?.duration ? data?.duration : 0)}</span>
                  </p>
                </div>
                <div className="detail">
                  <label>{t('Check-in Time')}</label>
                  <p>
                    {data?.slot &&
                      Utils.formatDateStringTz(
                        data?.slot?.start,
                        data?.selectedLocation?.timezone,
                        "DD MMM 'yy, hh:mma "
                      )}
                  </p>
                </div>
                <div className="detail">
                  <label>{t('Check-out Time')}</label>
                  <p>
                    {data?.slot &&
                      Utils.formatDateStringTz(
                        data?.slot?.end,
                        data?.selectedLocation?.timezone,
                        "DD MMM 'yy, hh:mma "
                      )}
                  </p>
                </div>
              </>
            )}
             {data?.petProfiles && data?.petProfiles.length > 0 && (
              <div className="detail align-start">
                <label>Pet Detail</label>
                <div className="pet-list-display">
                    {data.petProfiles.map((pet, index) => (
                      <PetConfirmationDisplay data={pet} showPrice={false} />
                    ))}
                  </div>
              </div>
            )}
            {data?.petProfiles && data?.petProfiles.length != data.noOfPets && data.noOfPets  && (
              <div className="detail align-start">
                <label>No of pets</label>
                <p>{data.noOfPets} {data.noOfPets > 1 ? 'Pets' : 'pet'}</p>
              </div>
            )}
            <div className="detail">
              <label>{t('Pet Parent Detail')}</label>
              {data?.parentProfile && (
                <p>
                  <span className="seperator">{data.parentProfile.name} </span>
                  <span>+{data.parentProfile.phone}</span>
                </p>
              )}
            </div>
            {servicePrice > 0 && (
              <>
                <div className="detail">
                  <label>{t('Credit Card')}</label>
                  {customerCard && (
                    <p className="credit-card-no">
                      xxxx xxxx xxxx <span>{last4Digit}</span>
                    </p>
                  )}
                </div>
                <div className="detail billing">
                  <label>{t('Billing Address')}</label>
                  {customerCard && (
                    <p>
                      <span className="seperator">
                        {customerCard.firstName} {customerCard.lastName}{' '}
                      </span>
                      <span>{data?.parentProfile && Utils.getAddress(data?.parentProfile)} </span>
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="action">
          <button
            className={`petsked-button button-dark ${servicePrice > 0 ? '' : 'center'} ${
              bookAppointmentMutation.isLoading ? 'loading' : ''
            }`}
            onClick={onBookAppoinment}
            disabled={bookAppointmentMutation.isLoading}
          >
            {bookAppointmentMutation.isLoading ? (
              <Loader text={'Confirming...'} />
            ) : servicePrice > 0 ? (
              <>
              
                <span>{t('Complete Booking')}</span>
                <span>{businessCurrency(business,'USD')} {getAdvancePayment()}</span>
              </>
            ) : (
              <span>{t('Complete Booking')}</span>
            )}
          </button>
          {data?.business?.termsOfService && data?.business?.termsOfService.length > 0 && (
            <p className="terms-and-condition">
              {t('By clicking')} {t("Complete Booking")} {t('you agree to the')}{' '}
              <a
                rel="noreferrer"
                href={window.location.origin + '/' + data?.business?.slug + '/terms'}
                target="_blank"
              >
                {t('Terms of Use')}
              </a>{' '}
              {t('of the provider.')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
